import { type GroupFilterOption } from "./group-filter";

/** Primary-layer chips, straight from the server's `firstLayerOptions`. */
export function getPrimaryOptions(
  firstLayerOptions: string[]
): GroupFilterOption[] {
  return firstLayerOptions.map((value) => ({ value, label: value }));
}

/**
 * Second-layer chips valid for the selected primary, from the server's
 * `optionsByFirstLayer` (already ordered + validity-filtered server-side). Empty
 * until a primary is picked; each option's value is the full composed groupName,
 * for a direct filter match.
 */
export function getSecondaryOptions(
  optionsByFirstLayer: Record<string, string[]>,
  selectedPrimary: string | null
): GroupFilterOption[] {
  if (!selectedPrimary) {
    return [];
  }

  return (optionsByFirstLayer[selectedPrimary] ?? []).map((secondary) => ({
    value: `${selectedPrimary}${secondary}`,
    label: secondary
  }));
}

/**
 * Maps every valid flat groupName to its primary-layer value, composing straight
 * from the server's `optionsByFirstLayer` (`${primary}${secondary}`). A primary
 * with no second layer maps to itself. This resolves a multi-char primary like
 * "10" correctly from "10A" (never mistaken for "1").
 */
export function buildGroupNameToPrimary(
  optionsByFirstLayer: Record<string, string[]>
): Map<string, string> {
  const groupNameToPrimary = new Map<string, string>();

  for (const [primary, secondaries] of Object.entries(optionsByFirstLayer)) {
    if (secondaries.length === 0) {
      groupNameToPrimary.set(primary, primary);
      continue;
    }

    for (const secondary of secondaries) {
      groupNameToPrimary.set(`${primary}${secondary}`, primary);
    }
  }

  return groupNameToPrimary;
}

/**
 * Narrows any grouped list by the active selection: a selected secondary (a
 * composed groupName) pins to that group, a primary alone keeps its whole group,
 * no selection passes through unchanged. Generic over the item type.
 */
export function filterByGroupSelection<T extends { groupName: string }>(
  items: T[],
  groupNameToPrimary: Map<string, string>,
  selectedPrimary: string | null,
  selectedSecondary: string | null
): T[] {
  let result = items;

  if (selectedPrimary) {
    result = result.filter(
      (item) => groupNameToPrimary.get(item.groupName) === selectedPrimary
    );
  }

  if (selectedSecondary) {
    result = result.filter((item) => item.groupName === selectedSecondary);
  }

  return result;
}
